import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CANDIDATE_SECURITY_STATUS,
  EVENT_DEDUP_WINDOW_MS,
  SECURITY_EVENTS,
  SECURITY_STATES
} from './constants';
import { mergePolicy } from './securityPolicy';
import { createSecurityEventLogger } from './securityEventLogger';
import { createEventDeduper } from './dedupeSecurityEvent';
import { enterFullscreen, isFullscreen, subscribeFullscreenChange } from './fullscreenManager';
import { subscribeVisibility } from './visibilityMonitor';
import { subscribeClipboardGuard } from './clipboardGuard';
import { subscribeKeyboardGuard } from './keyboardGuard';
import { subscribePrintGuard } from './printGuard';
import { subscribeBeforeUnload, subscribeInternalLinkGuard, subscribePopState } from './navigationGuard';
import { subscribeExternalLinkGuard } from './externalLinkGuard';
import { missingRequiredCapabilities } from './capabilities';
import { createTrustedInteractionScope } from './trustedInteraction';
import {
  describeScreenShareError,
  inspectDisplaySurface,
  screenShareDeniedEvent,
  startChunkRecorder,
  startScreenCapture,
  stopScreenCapture,
  subscribeTrackEnded
} from './screenCaptureManager';
import { SecureExamContext, disabledSecureExamValue } from './secureExamContext';
import SecurityBlockOverlay from './components/SecurityBlockOverlay';
import SecureExamPreflight from './components/SecureExamPreflight';
import { createSecurityRequest } from './http';
import { injectSecureExamStyles } from './injectSecureExamStyles';

function newId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `sess-${Date.now()}`;
}

export default function SecureExamProvider({
  enabled = false,
  examId,
  candidateId,
  policy: policyProp,
  apiBaseUrl,
  request: requestProp,
  websocketService = null,
  requireEnvironmentCheck = false,
  examLive = true,
  children
}) {
  const policy = useMemo(() => mergePolicy(policyProp), [policyProp]);
  const request = useMemo(
    () => requestProp || createSecurityRequest(apiBaseUrl),
    [apiBaseUrl, requestProp]
  );
  const sessionIdRef = useRef(null);
  const loggerRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const recordingIdRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const trusted = useRef(createTrustedInteractionScope()).current;
  const deduper = useRef(createEventDeduper(EVENT_DEDUP_WINDOW_MS)).current;
  const examActiveRef = useRef(false);

  const [securityState, setSecurityState] = useState(SECURITY_STATES.INITIALIZING);
  const [isFs, setIsFs] = useState(false);
  const [screenShareActive, setScreenShareActive] = useState(false);
  const [overlay, setOverlay] = useState(null);
  const [riskScore, setRiskScore] = useState(0);
  const [violationCounts, setViolationCounts] = useState({
    fullscreenExits: 0,
    tabSwitches: 0,
    focusLosses: 0,
    clipboardAttempts: 0,
    navigationAttempts: 0,
    screenShareStops: 0
  });
  const [answersLocked, setAnswersLocked] = useState(false);
  const [environmentCheckComplete, setEnvironmentCheckComplete] = useState(!requireEnvironmentCheck);
  const [shareError, setShareError] = useState('');

  const { caps, missing } = useMemo(() => missingRequiredCapabilities(policy), [policy]);

  const logSecurityEvent = useCallback(async (payload) => {
    if (!enabled || !policy.logSecurityEvents) return null;
    if (trusted.isTrusted() && ['WINDOW_BLUR', 'TAB_HIDDEN'].includes(payload.type)) {
      return loggerRef.current
        ? loggerRef.current.logSecurityEvent({
            ...payload,
            metadata: { ...(payload.metadata || {}), trustedInteraction: true, reason: trusted.getReason() }
          })
        : null;
    }
    if (deduper.dedupeSecurityEvent(payload.type) && payload.type !== SECURITY_EVENTS.TAB_VISIBLE) {
      return null;
    }
    if (payload.type === SECURITY_EVENTS.FULLSCREEN_EXITED) {
      setViolationCounts((c) => ({ ...c, fullscreenExits: c.fullscreenExits + 1 }));
    }
    if (payload.type === SECURITY_EVENTS.TAB_HIDDEN) {
      setViolationCounts((c) => ({ ...c, tabSwitches: c.tabSwitches + 1 }));
    }
    if (payload.type === SECURITY_EVENTS.WINDOW_BLUR) {
      setViolationCounts((c) => ({ ...c, focusLosses: c.focusLosses + 1 }));
    }
    if (['COPY_ATTEMPT', 'PASTE_ATTEMPT', 'CUT_ATTEMPT'].includes(payload.type)) {
      setViolationCounts((c) => ({ ...c, clipboardAttempts: c.clipboardAttempts + 1 }));
    }
    if (['NAVIGATION_ATTEMPT', 'EXTERNAL_LINK_ATTEMPT'].includes(payload.type)) {
      setViolationCounts((c) => ({ ...c, navigationAttempts: c.navigationAttempts + 1 }));
    }
    if (payload.type === SECURITY_EVENTS.SCREEN_SHARE_STOPPED) {
      setViolationCounts((c) => ({ ...c, screenShareStops: c.screenShareStops + 1 }));
    }
    return loggerRef.current ? loggerRef.current.logSecurityEvent(payload) : null;
  }, [deduper, enabled, policy.logSecurityEvents, trusted]);

  const uploadChunk = useCallback(async (blob) => {
    if (!recordingIdRef.current || !examId) return;
    const idx = chunkIndexRef.current;
    chunkIndexRef.current += 1;
    try {
      const res = await request(`/api/exams/${encodeURIComponent(examId)}/recordings/${recordingIdRef.current}/chunk-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chunkIndex: idx, contentType: blob.type || 'video/webm', byteSize: blob.size })
      });
      const data = await res.json().catch(() => ({}));
      if (data.uploadUrl) {
        await fetch(data.uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': blob.type || 'video/webm' } });
      }
    } catch (_) {
      /* recording is best-effort */
    }
  }, [examId, request]);

  const handleScreenShareEnded = useCallback(() => {
    setScreenShareActive(false);
    streamRef.current = null;
    logSecurityEvent({
      type: SECURITY_EVENTS.SCREEN_SHARE_STOPPED,
      severity: 'critical',
      message: 'Screen sharing was stopped'
    });
    if (examActiveRef.current && policy.detectScreenShareTermination) {
      setSecurityState(SECURITY_STATES.SECURITY_INTERRUPTION);
      setOverlay({
        reason: 'SCREEN_SHARE_STOPPED',
        title: 'Screen sharing stopped',
        message: 'Screen sharing is mandatory for this examination. This event has been recorded.',
        actionLabel: policy.reconnectScreenShareAllowed ? 'Resume Screen Sharing' : null
      });
    }
  }, [logSecurityEvent, policy.detectScreenShareTermination, policy.reconnectScreenShareAllowed]);

  const startScreenShare = useCallback(async () => {
    trusted.beginTrustedInteraction('SCREEN_SHARE_PERMISSION');
    setShareError('');
    try {
      const stream = await startScreenCapture();
      const inspect = inspectDisplaySurface(stream);
      streamRef.current = stream;
      setScreenShareActive(true);
      subscribeTrackEnded(stream, handleScreenShareEnded);
      await logSecurityEvent({
        type: SECURITY_EVENTS.SCREEN_SHARE_STARTED,
        severity: 'info',
        message: 'Screen sharing started',
        metadata: {
          displaySurface: inspect.displaySurface,
          verified: inspect.verified,
          preferMonitor: inspect.preferMonitor
        }
      });
      if (examId && sessionIdRef.current) {
        try {
          const recRes = await request(`/api/exams/${encodeURIComponent(examId)}/recordings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ candidateId, sessionId: sessionIdRef.current, recordingType: 'screen' })
          });
          const rec = await recRes.json().catch(() => ({}));
          recordingIdRef.current = rec.recordingId || null;
          chunkIndexRef.current = 0;
          recorderRef.current = startChunkRecorder(stream, uploadChunk);
        } catch (err) {
          await logSecurityEvent({
            type: SECURITY_EVENTS.SCREEN_SHARE_ERROR,
            message: 'Recording upload could not start',
            metadata: { error: err && err.message }
          });
        }
      }
      setOverlay(null);
      setSecurityState((prev) => {
        const afterShare = [
          SECURITY_STATES.CHECKING_REQUIREMENTS,
          SECURITY_STATES.WAITING_FOR_SCREEN_SHARE,
          SECURITY_STATES.SECURITY_INTERRUPTION,
          SECURITY_STATES.REMEDIATION_REQUIRED
        ].includes(prev);
        if (!afterShare) return prev;
        if (policy.requireFullscreen && !isFullscreen()) return SECURITY_STATES.WAITING_FOR_FULLSCREEN;
        return SECURITY_STATES.SECURED;
      });
    } catch (err) {
      await logSecurityEvent({
        ...screenShareDeniedEvent(),
        metadata: { error: err && err.message, name: err && err.name }
      });
      setShareError(describeScreenShareError(err));
    } finally {
      setTimeout(() => trusted.endTrustedInteraction(), 800);
    }
  }, [candidateId, examId, handleScreenShareEnded, logSecurityEvent, policy.requireFullscreen, request, trusted, uploadChunk]);

  const requestFullscreen = useCallback(async () => {
    try {
      await enterFullscreen();
      if (!isFullscreen()) {
        setIsFs(false);
        return;
      }
      setIsFs(true);
      await logSecurityEvent({
        type: SECURITY_EVENTS.FULLSCREEN_ENTERED,
        message: 'Candidate entered fullscreen mode'
      });
      setOverlay(null);
      setSecurityState((prev) => {
        if (policy.requireScreenShare && !streamRef.current) return SECURITY_STATES.WAITING_FOR_SCREEN_SHARE;
        return SECURITY_STATES.SECURED;
      });
    } catch (err) {
      await logSecurityEvent({
        type: SECURITY_EVENTS.FULLSCREEN_REENTRY_FAILED,
        message: 'Fullscreen re-entry failed',
        metadata: { error: err && err.message }
      });
    }
  }, [logSecurityEvent, policy.requireScreenShare]);

  useEffect(() => {
    examActiveRef.current = securityState === SECURITY_STATES.EXAM_ACTIVE;
  }, [securityState]);

  useEffect(() => {
    if (!enabled || !examId || !candidateId) return undefined;
    let cancelled = false;
    (async () => {
      setSecurityState(SECURITY_STATES.INITIALIZING);
      const reloadKey = `cb-reload:${examId}:${candidateId}`;
      const hadReload = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(reloadKey);
      sessionStorage.setItem(reloadKey, '1');

      let sessionJson = {};
      try {
        const sessionRes = await request(`/api/exams/${encodeURIComponent(examId)}/security-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ candidateId, sessionId: newId() })
        });
        sessionJson = sessionRes && typeof sessionRes.json === 'function'
          ? await sessionRes.json().catch(() => ({}))
          : {};
      } catch (_) {
        sessionJson = {};
      }
      if (cancelled) return;
      sessionIdRef.current = (sessionJson.session && sessionJson.session.sessionId) || newId();
      loggerRef.current = createSecurityEventLogger({
        examId,
        candidateId,
        getSessionId: () => sessionIdRef.current,
        websocketService,
        request
      });
      if (hadReload) {
        await loggerRef.current.logSecurityEvent({
          type: SECURITY_EVENTS.PAGE_RELOAD_DETECTED,
          severity: 'medium',
          message: 'Exam page was reloaded'
        });
      }
      await loggerRef.current.logSecurityEvent({
        type: sessionJson.recovered ? SECURITY_EVENTS.EXAM_SESSION_RESTORED : SECURITY_EVENTS.EXAM_SECURITY_STARTED,
        message: sessionJson.recovered ? 'Security session restored' : 'Closed-book security session started'
      });
      setSecurityState(SECURITY_STATES.CHECKING_REQUIREMENTS);
    })();
    return () => {
      cancelled = true;
    };
  }, [candidateId, enabled, examId, request, websocketService]);

  useEffect(() => {
    if (!enabled || !examLive) return undefined;
    injectSecureExamStyles();
    document.body.classList.add('secure-exam-print-block');
    if (policy.blockTextSelection) document.body.classList.add('secure-exam-content');

    const unsubFs = subscribeFullscreenChange(() => {
      const fs = isFullscreen();
      setIsFs(fs);
      if (!fs && examActiveRef.current && policy.detectFullscreenExit) {
        logSecurityEvent({
          type: SECURITY_EVENTS.FULLSCREEN_EXITED,
          message: 'Candidate exited fullscreen mode',
          metadata: { pathname: window.location.pathname }
        });
        setSecurityState(SECURITY_STATES.SECURITY_INTERRUPTION);
        setOverlay({
          reason: 'FULLSCREEN_REQUIRED',
          title: 'Fullscreen mode was exited',
          message: 'Your exam activity has been logged.',
          actionLabel: 'Return to Fullscreen'
        });
      }
    });

    const unsubVis = subscribeVisibility(
      (evt) => {
        if (trusted.isTrusted()) {
          return logSecurityEvent({ ...evt, metadata: { ...(evt.metadata || {}), trustedInteraction: true } });
        }
        return logSecurityEvent(evt);
      },
      { enabled: policy.detectVisibilityChange || policy.detectTabSwitch }
    );

    const onBlur = () => {
      if (!policy.detectWindowBlur) return;
      if (deduper.dedupeSecurityEvent('WINDOW_BLUR_BUNDLE', 500)) return;
      logSecurityEvent({
        type: SECURITY_EVENTS.WINDOW_BLUR,
        message: 'Exam window lost focus',
        metadata: trusted.isTrusted() ? { trustedInteraction: true } : {}
      });
    };
    const onFocus = () => {
      if (!policy.detectWindowBlur) return;
      logSecurityEvent({ type: SECURITY_EVENTS.WINDOW_FOCUS, message: 'Exam window regained focus' });
    };
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);

    const unsubClip = subscribeClipboardGuard(logSecurityEvent, policy);
    const unsubKeys = subscribeKeyboardGuard(logSecurityEvent, policy);
    const unsubPrint = subscribePrintGuard(logSecurityEvent, policy);
    const unsubNav = subscribeBeforeUnload(() => examActiveRef.current, policy);
    const unsubPop = subscribePopState(logSecurityEvent, () => examActiveRef.current, policy);
    const unsubInternal = subscribeInternalLinkGuard(logSecurityEvent, () => examActiveRef.current, policy);
    const unsubLinks = subscribeExternalLinkGuard(logSecurityEvent, policy);

    return () => {
      document.body.classList.remove('secure-exam-print-block');
      document.body.classList.remove('secure-exam-content');
      unsubFs();
      unsubVis();
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      unsubClip();
      unsubKeys();
      unsubPrint();
      unsubNav();
      unsubPop();
      unsubInternal();
      unsubLinks();
    };
  }, [deduper, enabled, examLive, logSecurityEvent, policy, trusted]);

  const beginExam = useCallback(() => {
    setSecurityState(SECURITY_STATES.EXAM_ACTIVE);
    examActiveRef.current = true;
    // eslint-disable-next-line no-console
    console.log('[ClosedBook] enforcement armed — exam is live');
  }, []);

  const beginCheck = useCallback(() => {
    if (policy.requireScreenShare && !streamRef.current) {
      setSecurityState(SECURITY_STATES.WAITING_FOR_SCREEN_SHARE);
      return;
    }
    if (policy.requireFullscreen) {
      setSecurityState(SECURITY_STATES.WAITING_FOR_FULLSCREEN);
      return;
    }
    setSecurityState(SECURITY_STATES.SECURED);
  }, [policy.requireFullscreen, policy.requireScreenShare]);

  const markEnvironmentCheckComplete = useCallback(() => {
    setEnvironmentCheckComplete(true);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const fsOk = !policy.requireFullscreen || isFs;
    const ssOk = !policy.requireScreenShare || screenShareActive;
    const waiting = [
      SECURITY_STATES.CHECKING_REQUIREMENTS,
      SECURITY_STATES.WAITING_FOR_FULLSCREEN,
      SECURITY_STATES.WAITING_FOR_SCREEN_SHARE,
      SECURITY_STATES.REMEDIATION_REQUIRED
    ].includes(securityState);
    if (fsOk && ssOk && waiting) {
      setSecurityState(SECURITY_STATES.SECURED);
    }
  }, [enabled, isFs, policy.requireFullscreen, policy.requireScreenShare, screenShareActive, securityState]);

  const overlayAction = useCallback(async () => {
    if (!overlay) return;
    if (overlay.reason === 'FULLSCREEN_REQUIRED' || overlay.actionLabel === 'Return to Fullscreen') {
      await requestFullscreen();
      return;
    }
    if (overlay.reason === 'SCREEN_SHARE_STOPPED' || overlay.reason === 'SCREEN_SHARE_REQUIRED') {
      await startScreenShare();
      return;
    }
    setOverlay(null);
  }, [overlay, requestFullscreen, startScreenShare]);

  const finalizeAndSubmit = useCallback(async () => {
    setAnswersLocked(true);
    if (loggerRef.current) await loggerRef.current.flush();
    if (recorderRef.current) await recorderRef.current.stop();
    if (recordingIdRef.current && examId) {
      await request(`/api/exams/${encodeURIComponent(examId)}/recordings/${recordingIdRef.current}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      }).catch(() => {});
    }
    stopScreenCapture(streamRef.current);
    streamRef.current = null;
    setScreenShareActive(false);
    if (examId && sessionIdRef.current) {
      await request(`/api/exams/${encodeURIComponent(examId)}/security-session/${sessionIdRef.current}/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'SUBMITTED' })
      }).catch(() => {});
    }
    setSecurityState(SECURITY_STATES.SUBMITTED);
    examActiveRef.current = false;
  }, [examId, request]);

  const canStartExam =
    securityState === SECURITY_STATES.SECURED &&
    (!requireEnvironmentCheck || environmentCheckComplete) &&
    (!policy.requireFullscreen || isFs) &&
    (!policy.requireScreenShare || screenShareActive);

  useEffect(() => {
    if (!enabled || !examLive || answersLocked) return;
    if (!canStartExam) return;
    if (securityState === SECURITY_STATES.EXAM_ACTIVE) return;
    beginExam();
  }, [answersLocked, beginExam, canStartExam, enabled, examLive, securityState]);

  const canInteractWithExam = !enabled || (securityState === SECURITY_STATES.EXAM_ACTIVE && !overlay && !answersLocked);

  const securityStatus = !screenShareActive && policy.requireScreenShare && examActiveRef.current
    ? CANDIDATE_SECURITY_STATUS.NON_COMPLIANT
    : overlay
      ? CANDIDATE_SECURITY_STATUS.NON_COMPLIANT
      : riskScore >= 40
        ? CANDIDATE_SECURITY_STATUS.WARNING
        : CANDIDATE_SECURITY_STATUS.SECURE;

  const value = {
    enabled: true,
    securityStatus,
    securityState,
    isFullscreen: isFs,
    enterFullscreen: requestFullscreen,
    screenShareActive,
    screenShareStream: streamRef.current,
    startScreenShare,
    currentRiskScore: riskScore,
    setCurrentRiskScore: setRiskScore,
    violationCounts,
    securitySessionId: sessionIdRef.current,
    canStartExam,
    canInteractWithExam,
    environmentCheckComplete,
    markEnvironmentCheckComplete,
    logSecurityEvent,
    flushSecurityEvents: () => (loggerRef.current ? loggerRef.current.flush() : Promise.resolve()),
    finalizeAndSubmit,
    pauseTimerOnSecurityInterruption: !!policy.pauseTimerOnSecurityInterruption,
    policy,
    overlay
  };

  if (!enabled) {
    return <SecureExamContext.Provider value={disabledSecureExamValue}>{children}</SecureExamContext.Provider>;
  }

  const waitingForEnvironment = requireEnvironmentCheck && !environmentCheckComplete;
  const examUnlocked = [SECURITY_STATES.EXAM_ACTIVE, SECURITY_STATES.SUBMITTED, SECURITY_STATES.TERMINATED].includes(securityState);
  const showPreflight = !!examLive && !waitingForEnvironment && !examUnlocked;

  return (
    <SecureExamContext.Provider value={value}>
      {children}
      {showPreflight ? (
        <SecureExamPreflight
          capabilities={caps}
          missing={missing}
          policy={policy}
          isFullscreen={isFs}
          screenShareActive={screenShareActive}
          environmentCheckComplete={environmentCheckComplete || !requireEnvironmentCheck}
          requireEnvironmentCheck={requireEnvironmentCheck}
          onEnterFullscreen={requestFullscreen}
          onStartScreenShare={startScreenShare}
          shareError={shareError}
          onBeginCheck={beginCheck}
          canStartExam={canStartExam}
          onStartExam={beginExam}
          phase={securityState}
        />
      ) : null}
      <SecurityBlockOverlay
        open={!!overlay && securityState !== SECURITY_STATES.SUBMITTED}
        title={overlay && overlay.title}
        message={overlay && overlay.message}
        actionLabel={overlay && overlay.actionLabel}
        onAction={overlayAction}
      />
    </SecureExamContext.Provider>
  );
}
